/* eslint-disable react-refresh/only-export-components */
/**
 * SageAssistant.tsx
 *
 * Thin orchestrator for the Sage co-pilot.
 * Manages state, tRPC hooks, SSE streaming, and session persistence.
 * Delegates all rendering to SagePanel (content) and SageTriggerButton (FAB).
 *
 * On desktop (≥768px): fixed right panel with drag-to-resize handle.
 * On mobile (<768px): Vaul drawer from the right.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { trpc } from "@/lib/trpc";
import {
  SAGE_STREAM_URL,
  SAGE_SESSION_KEY,
  FEATURE_SUGGESTED_QUESTIONS,
  PANEL_HEIGHT_EMPTY,
  PANEL_HEIGHT_PER_MSG,
  PANEL_HEIGHT_MIN,
  PANEL_HEIGHT_MAX_PX,
  PANEL_MAX_VH_DESKTOP,
  PANEL_BOTTOM_OFFSET,
  type ResponseMode,
} from "./config";
import SagePanel, { type Message } from "./SagePanel";
import SageTriggerButton from "./SageTriggerButton";
import {
  Drawer,
  DrawerContent,
} from "@/components/ui/drawer";

// ── Page-specific greeting messages ──────────────────────────────────────────
const PAGE_GREETINGS: Record<string, string> = {
  starthere:   "Here's the thing: 65% of U.S. adults fall outside at least one traditional norm. Not fringe. Not alternative. The actual majority — just invisible. This platform makes that visible. I can walk you through the thesis, decode the data, or help you figure out where you fit in the picture. What are you curious about?",
  venn:        "Three circles. One uncomfortable truth. The centre — the overlap of all three norms — is smaller than most people assume. What's wild is how much of the population lives in the crescents, not the middle. Want to understand what each zone actually represents?",
  radar:       "Six axes. Six profiles. One spiderweb that tells a story no bar chart can. The shape of the polygon is where the meaning lives — not any single number. Toggle a profile, move a slider, and watch the geometry change. What do you want to understand about this chart?",
  r3d:         "Same data, third dimension. When you rotate this view, you start to see something the flat chart hides: the profiles don't just differ in size — they differ in *direction*. Which profile are you most curious about?",
  explainer:   "Seven scenes. Each one teaches you a different way to read the radar. By the end, you'll see things in the data that most people walk right past. Want to start from the beginning, or jump to a specific pattern?",
  hypothesis:  "The thesis has three moves: a demographic shift, an ethical distinction, and a visibility gap. Each one is uncomfortable in a different way. Which one do you want to pull apart?",
  references:  "Every number in this thesis has a source. Gallup, Kinsey, the Survey Center on American Life, the General Social Survey. If you want to check the math, I can walk you through any of it. What are you looking for?",
  datasources: "The six axes aren't invented — each one is derived from a specific body of peer-reviewed research. Ask me how any axis is defined, where the data comes from, or why we weighted it the way we did.",
  pitchdeck:   "This is the Lola & Ber investment overview — the business case built on top of the Redefining Normal™ thesis. Ask me about the market opportunity, the data, or the people behind it.",
  help:        "Lost? Curious? Just want to understand what you're looking at? I'm here for all of it. Ask me anything — the thesis, the charts, the research, or what actually happens at a What If retreat.",
  admin:       "Settings mode. I can help you understand the knowledge base, explain what any setting does, or walk through the data pipeline. What do you need?",
  instagram:   "The @lolaandber feed is where the thesis meets real life — behind-the-scenes retreat moments, data visualisations, and the kind of conversations most people only have at 2am. What are you looking for in the feed?",
};

const DEFAULT_GREETING = "I'm Sage — the guide to Lola & Ber's What If world. I know the Redefining Normal™ data cold, and I have opinions about it. Ask me anything.";

// ── Page descriptions for Sage context ──────────────────────────────────────
export const PAGE_DESCRIPTIONS: Record<string, string> = {
  starthere:   "The Start Here page — thesis overview, key stats, and navigation pathways.",
  venn:        "The Venn Diagram page — a visual overview of the three normative dimensions.",
  radar:       "The Radar 2D page — an interactive radar chart comparing six population profiles.",
  r3d:         "The Radar 3D page — a three-dimensional ice-chip plane visualisation.",
  hypothesis:  "The Hypothesis page — the conceptual framework and argument behind the thesis.",
  help:        "The Help page — guide, data sources, FAQs, and glossary.",
  references:  "The References page — peer-reviewed citations and data sources.",
  datasources: "The Data Sources page — axis definitions and methodology.",
  pitchdeck:   "The Pitch Deck page — the Lola & Ber investment overview.",
  admin:       "The Settings page — forecast data, source library, and app settings.",
  explainer:   "The Reading the Radar page — a guided 7-scene walkthrough of how to read the six-axis radar graph.",
};

// ── Avatar URLs ──────────────────────────────────────────────────────────────
const AVATAR_URLS = {
  manga: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663270229297/JyWoQIxOvsRfbwxQ.png",
  photo: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663270229297/vRImtCRLgursjHDj.png",
} as const;

// ── Props ────────────────────────────────────────────────────────────────────
export interface SageAssistantProps {
  activePage?: string;
  pageTitle?: string;
}

// ── Hook to detect mobile viewport ───────────────────────────────────────────
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    setIsMobile(mq.matches); // eslint-disable-line react-hooks/set-state-in-effect -- sync initial media query state
    return () => mq.removeEventListener("change", handler);
  }, [breakpoint]);
  return isMobile;
}

// ── Resizable panel width (desktop) ──────────────────────────────────────────
const MIN_WIDTH = 320;
const MAX_WIDTH = 600;
const DEFAULT_WIDTH = 380;

// ── Compute adaptive panel height based on message count ─────────────────────
function useAdaptivePanelHeight(messageCount: number): { height: number; isAtMax: boolean } {
  const [vh, setVh] = useState(() => window.innerHeight);
  useEffect(() => {
    const handler = () => setVh(window.innerHeight);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // Message pairs (user + assistant = 1 turn)
  const turns  = Math.ceil(messageCount / 2);
  const desired = PANEL_HEIGHT_EMPTY + turns * PANEL_HEIGHT_PER_MSG;
  const maxPx   = Math.min(PANEL_HEIGHT_MAX_PX, (vh * PANEL_MAX_VH_DESKTOP) / 100);
  const height  = Math.max(PANEL_HEIGHT_MIN, Math.min(desired, maxPx));
  const isAtMax = height >= maxPx - 4;
  return { height, isAtMax };
}

// ── Main Orchestrator ────────────────────────────────────────────────────────
export default function SageAssistant({ activePage, pageTitle }: SageAssistantProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [mode, setMode] = useState<ResponseMode>("concise");
  const [panelWidth, setPanelWidth] = useState(DEFAULT_WIDTH);
  const [sessionId] = useState(() => {
    const stored = sessionStorage.getItem(SAGE_SESSION_KEY);
    if (stored) return stored;
    const id = `sage-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(SAGE_SESSION_KEY, id);
    return id;
  });

  const [historyLoaded, setHistoryLoaded] = useState(false);
  // eslint-disable-next-line react-hooks/purity -- intentional: capture session start timestamp once
  const sessionStartedAt = useRef<number>(Date.now());
  const abortRef = useRef<AbortController | null>(null);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const isMobile = useIsMobile();

  // ── Listen for sage:open custom event (from StartHerePage CTA) ─────────────
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener("sage:open", handler);
    return () => window.removeEventListener("sage:open", handler);
  }, []);

  // ── tRPC hooks ──────────────────────────────────────────────────────────────
  const { data: historyData } = trpc.sage.loadConversation.useQuery(
    { sessionId },
    { enabled: !historyLoaded, staleTime: Infinity }
  );
  const saveConversation = trpc.sage.saveConversation.useMutation();
  const clearConversationMutation = trpc.sage.clearConversation.useMutation();

  useEffect(() => {
    if (historyLoaded || !historyData) return;
    if (historyData.messages.length > 0) {
      setMessages( // eslint-disable-line react-hooks/set-state-in-effect -- intentional: hydrate local state from server history
        historyData.messages.map((m) => ({
          role: m.role as "user" | "assistant",
          content: m.content,
          timestamp: m.timestamp,
        }))
      );
    }
    setHistoryLoaded(true);  
  }, [historyData, historyLoaded]);

  const { data: enabledData } = trpc.settings.getSageEnabled.useQuery(
    undefined,
    { staleTime: 30_000 }
  );
  const isSageEnabled = enabledData?.enabled ?? true;

  const { data: sageNameData } = trpc.settings.getSageName.useQuery(
    undefined,
    { staleTime: 60_000 }
  );
  const sageName = sageNameData?.name ?? "Sage";

  const { data: avatarStyleData } = trpc.settings.getSageAvatar.useQuery(
    undefined,
    { staleTime: 60_000 }
  );
  const activeAvatarUrl = AVATAR_URLS[avatarStyleData?.avatar ?? "manga"];

  const { data: suggestionsData } = trpc.sage.suggestedQuestions.useQuery(
    { count: 4, pageContext: activePage },
    { enabled: FEATURE_SUGGESTED_QUESTIONS && messages.length === 0 && isSageEnabled }
  );

  // ── Drag-to-resize (desktop) ───────────────────────────────────────────────
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startWidth: panelWidth };

    const handleMouseMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      const delta = dragRef.current.startX - ev.clientX; // dragging left = wider
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, dragRef.current.startWidth + delta));
      setPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      dragRef.current = null;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [panelWidth]);

  // ── Streaming send ─────────────────────────────────────────────────────────
  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || isStreaming) return;

    const userMsg: Message = { role: "user", content: text.trim(), timestamp: Date.now() };
    const isFirst = messages.length === 0;

    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setIsStreaming(true);

    const assistantMsg: Message = { role: "assistant", content: "", timestamp: Date.now() };
    setMessages((prev) => [...prev, assistantMsg]);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const history = messages.map((m) => ({ role: m.role, content: m.content }));

      const res = await fetch(SAGE_STREAM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          question: text.trim(),
          history,
          mode,
          isFirst,
          sessionId,
          sessionStartedAt: sessionStartedAt.current,
          pageContext: activePage
            ? { page: activePage, title: pageTitle ?? activePage }
            : undefined,
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const reader = res.body?.getReader();
      if (!reader) throw new Error("No stream");

      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith("data: ")) continue;
          try {
            const event = JSON.parse(trimmed.slice(6));
            if (event.type === "token") {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === "assistant") {
                  updated[updated.length - 1] = { ...last, content: last.content + event.token };
                }
                return updated;
              });
            } else if (event.type === "error") {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === "assistant" && !last.content) {
                  updated[updated.length - 1] = { ...last, content: event.message ?? "Something went wrong. Please try again." };
                }
                return updated;
              });
            }
          } catch {
            // malformed chunk
          }
        }
      }
    } catch (err: unknown) {
      if ((err as Error)?.name !== "AbortError") {
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.role === "assistant" && !last.content) {
            updated[updated.length - 1] = { ...last, content: "I got a bit tangled — please try again." };
          }
          return updated;
        });
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
      setMessages((current) => {
        const newTurns = current.slice(-(2));
        if (newTurns.length > 0) {
          saveConversation.mutate({
            sessionId,
            messages: newTurns.map((m) => ({
              role: m.role,
              content: m.content,
              timestamp: m.timestamp,
              page: activePage,
            })),
          });
        }
        return current;
      });
    }
  }, [messages, isStreaming, mode, sessionId, activePage, pageTitle, saveConversation]);

  const clearConversation = useCallback(() => {
    if (isStreaming) abortRef.current?.abort();
    setMessages([]);
    setIsStreaming(false);
    setHistoryLoaded(false);
    clearConversationMutation.mutate({ sessionId });
  }, [isStreaming, sessionId, clearConversationMutation]);

  // ── Adaptive height (desktop — must be called unconditionally for hooks rules) ──
  const { height: panelHeight, isAtMax: isAtMaxHeight } = useAdaptivePanelHeight(messages.length);

  // Hide entirely when disabled via Admin toggle
  if (!isSageEnabled) return null;

  // Derived values
  const greeting = (activePage && PAGE_GREETINGS[activePage]) || DEFAULT_GREETING;
  const pageBadge = pageTitle && activePage && activePage !== "admin" ? pageTitle : undefined;
  const hasPendingSuggestion = !!(suggestionsData?.questions && suggestionsData.questions.length > 0 && messages.length === 0);

  // Common panel props
  const panelProps = {
    messages,
    input,
    setInput,
    isStreaming,
    mode,
    setMode,
    sageName,
    avatarUrl: activeAvatarUrl,
    activePage,
    pageTitle,
    pageBadge,
    greeting,
    suggestions: suggestionsData?.questions,
    onSend: sendMessage,
    onClear: clearConversation,
    onClose: () => setIsOpen(false),
  };

  // ── Mobile: Vaul drawer from right ─────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        <SageTriggerButton
          onClick={() => setIsOpen(true)}
          hasPendingSuggestion={hasPendingSuggestion}
          isOpen={isOpen}
        />
        <Drawer open={isOpen} onOpenChange={setIsOpen} direction="right">
          <DrawerContent
            className="h-full w-[85vw] max-w-[400px] ml-auto p-0 rounded-none"
            style={{
              maxHeight: "100dvh",
              /* iOS: use -webkit-fill-available as fallback for keyboard avoidance */
              height: "-webkit-fill-available",
            } as React.CSSProperties}
          >
            <SagePanel {...panelProps} />
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  // ── Desktop: adaptive floating panel anchored bottom-right ────────────────
  return (
    <>
      <SageTriggerButton
        onClick={() => setIsOpen(true)}
        hasPendingSuggestion={hasPendingSuggestion}
        isOpen={isOpen}
      />
      {isOpen && (
        <div
          className="fixed right-0 z-40 flex"
          style={{
            width: panelWidth,
            height: panelHeight,
            bottom: isAtMaxHeight ? 0 : PANEL_BOTTOM_OFFSET,
            top: isAtMaxHeight ? 0 : "auto",
            transition: "height 0.25s cubic-bezier(0.4, 0, 0.2, 1), bottom 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {/* Drag handle */}
          <div
            className="w-1.5 cursor-col-resize flex items-center justify-center hover:bg-primary/10 transition-colors group shrink-0"
            onMouseDown={handleDragStart}
            title="Drag to resize"
          >
            <div className="w-0.5 h-8 rounded-full bg-border group-hover:bg-primary/40 transition-colors" />
          </div>
          {/* Panel content */}
          <div className="flex-1 min-w-0 h-full border-l border-border bg-background shadow-xl rounded-tl-xl overflow-hidden">
            <SagePanel {...panelProps} />
          </div>
        </div>
      )}
    </>
  );
}
